import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  input,
  OnInit,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule } from '@angular/forms';
import { PartnersUtilsService } from '@app/modules/partners/partners-utils.service';
import { Partner } from '@core/core.types';
import {
  NgbHighlight,
  NgbTypeahead,
  NgbTypeaheadSelectItemEvent,
} from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  merge,
  Observable,
  startWith,
  Subject,
  switchMap,
} from 'rxjs';

@Component({
  selector: 'app-partner-typeahead',
  templateUrl: './partner-typeahead.component.html',
  styleUrls: ['./partner-typeahead.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgClass, NgbTypeahead, NgbHighlight, FormsModule, TranslateModule],
})
export class PartnerTypeaheadComponent implements OnInit {
  private partnersUtils = inject(PartnersUtilsService);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);

  public control = input<FormControl<string>>();
  public partnerSelected = output<Partner>();
  public ids = input<string[]>();
  public prepend = input<Partner[]>([]);
  public placeholder = input<string>('shared.partnerTypeahead.placeholder');
  public fieldSize = input<string>('tiny');
  public showRemoveButton = input<boolean>(false);
  public disabled = input<boolean>(false);
  public clearOnSelect = input<boolean>(false);

  protected focus$ = new Subject<string>();
  protected click$ = new Subject<string>();
  protected partnerModel = signal<Partner>(null);
  protected partnerFormatter = (partner: Partner) => partner.name;

  private htmlTypeaheadElement = viewChild<ElementRef<HTMLInputElement>>(
    'htmlTypeaheadElement'
  );
  private instance = viewChild<NgbTypeahead>('instance');
  private lastSearchTerm = '';

  ngOnInit(): void {
    this.control()
      .valueChanges.pipe(
        startWith(this.control().value),
        switchMap((value) => this.partnersUtils.getPartnerById$(value)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((value) => {
        if (!value) {
          const inputValue =
            this.htmlTypeaheadElement()?.nativeElement.value ?? '';

          if (!inputValue || inputValue === this.lastSearchTerm) {
            this.clearInput();
          }
        } else if (!this.lastSearchTerm) {
          this.partnerModel.set(value);
        }

        this.cdr.markForCheck();
      });
  }

  protected search = (text$: Observable<string>) => {
    const searchText$ = text$.pipe(distinctUntilChanged());
    const clicksWithClosedPopup$ = this.click$.pipe(
      filter(() => (this.instance() ? !this.instance().isPopupOpen() : false))
    );
    const inputFocus$ = this.focus$;

    return merge(searchText$, inputFocus$, clicksWithClosedPopup$).pipe(
      debounceTime(500),
      switchMap((term) => {
        if (
          this.lastSearchTerm !== term &&
          this.control().value &&
          !this.showRemoveButton()
        ) {
          this.control().setValue(null);
          this.partnerSelected.emit(null);
        }

        this.lastSearchTerm = term;

        if (!term) {
          this.control().setValue(null);
        }

        this.cdr.markForCheck();

        return this.partnersUtils
          .getPartnersBySearchString(term, this.ids())
          .pipe(
            map((partners) => {
              partners = [...this.prepend(), ...partners];
              if (partners && partners.length > 0) {
                return partners.filter((p) => !p.deactivated);
              }
              return null;
            })
          );
      })
    );
  };

  protected selectItem(selectItemEvent: NgbTypeaheadSelectItemEvent) {
    const selectedPartner = selectItemEvent.item as Partner;
    if (this.clearOnSelect()) {
      selectItemEvent.preventDefault();
    }
    this.control().setValue(selectedPartner.id);
    this.partnerSelected.emit(selectedPartner);
    if (this.clearOnSelect()) {
      this.clearInput();
    } else {
      this.lastSearchTerm = selectedPartner.name;
    }
  }

  protected removeItem() {
    this.control().setValue(null);
    this.partnerSelected.emit(null);
    this.clearInput();
  }

  private clearInput() {
    this.lastSearchTerm = '';
    this.partnerModel.set(null);
    const inputElement = this.htmlTypeaheadElement()?.nativeElement;
    if (inputElement) {
      inputElement.value = '';
    }
    this.instance()?.dismissPopup();
  }
}
